// Environment configuration for different deployment scenarios
const getApiBaseUrl = () => {
  // Check if we're in a browser environment
  if (typeof window === 'undefined') {
    return 'http://localhost:3001/api';
  }

  // Debug logging
  console.log('🔍 Environment Debug Info:', {
    hostname: window.location.hostname,
    port: window.location.port,
    protocol: window.location.protocol,
    VITE_API_BASE_URL: import.meta.env.VITE_API_BASE_URL,
    BACKEND_IP: import.meta.env.BACKEND_IP,
    VITE_ENVIRONMENT: import.meta.env.VITE_ENVIRONMENT,
    allEnvVars: import.meta.env
  });

  // Priority 1: Check for environment variable override (Vite environment variables)
  const envApiUrl = import.meta.env.VITE_API_BASE_URL;
  if (envApiUrl) {
    console.log('✅ Using VITE_API_BASE_URL:', envApiUrl);
    return envApiUrl;
  }

  // Priority 2: Check for static backend IP from environment variables
  const backendIp = import.meta.env.BACKEND_IP;
  if (backendIp) {
    return `http://${backendIp}:3001/api`;
  }

  // Priority 3: Get the current hostname and protocol for fallback
  const { protocol, hostname, port } = window.location;
  
  // Check if we're running in local development (Vite dev server typically runs on port 3000)
  const isLocalDev = port === '3000' || 
                     hostname === 'localhost' || 
                     hostname === '127.0.0.1' || 
                     hostname.startsWith('192.168.') || 
                     hostname.startsWith('10.') || 
                     hostname.startsWith('172.');
  
  // Check if we're running in a Docker container by looking for specific Docker indicators
  const isDocker = (hostname === 'frontend' || 
                   hostname.includes('docker') || 
                   import.meta.env.VITE_ENVIRONMENT === 'docker') &&
                   !isLocalDev; // Don't treat local dev as Docker even if hostname matches
  
  // Environment-specific configuration
  const environments = {
    // Local development - always use localhost for backend when developing locally
    'localhost': 'http://localhost:3001/api',
    'localdev': 'http://localhost:3001/api',
    
    // Docker bridge networking - use service name
    'docker': 'http://backend:3001/api',
    
    // Production environments - adjust these based on your actual domains
    'your-prod-domain.com': `${protocol}//your-prod-domain.com:3001/api`,
    'your-qa-domain.com': `${protocol}//your-qa-domain.com:3001/api`,
    'your-dev-domain.com': `${protocol}//your-dev-domain.com:3001/api`,
  };

  console.log('🔍 Environment Detection:', {
    isLocalDev,
    isDocker,
    hostname,
    port,
    environments
  });

  // If we're in local development, always use localhost for backend
  if (isLocalDev) {
    console.log('✅ Detected LOCAL DEVELOPMENT - using localhost backend');
    return environments.localdev;
  }
  
  // If we're in Docker, use Docker configuration
  if (isDocker) {
    console.log('✅ Detected DOCKER - using Docker backend');
    return environments.docker;
  }

  // Return environment-specific URL or default to localhost
  const fallbackUrl = environments[hostname] || environments['localhost'];
  console.log('✅ Using FALLBACK URL:', fallbackUrl);
  return fallbackUrl;
};

// Export the API base URL
export const API_BASE = getApiBaseUrl();

// Export a function to get the current environment
export const getCurrentEnvironment = () => {
  if (typeof window === 'undefined') return 'server';
  
  const { hostname } = window.location;
  
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'development';
  }
  
  if (hostname === 'frontend' || hostname.includes('docker')) {
    return 'docker';
  }
  
  if (hostname.includes('prod')) {
    return 'production';
  }
  
  if (hostname.includes('qa')) {
    return 'qa';
  }
  
  if (hostname.includes('dev')) {
    return 'dev';
  }
  
  return 'unknown';
};

// Debug helper to log the current configuration
export const logEnvironmentConfig = () => {
  console.log('Environment Configuration:', {
    hostname: typeof window !== 'undefined' ? window.location.hostname : 'server',
    environment: getCurrentEnvironment(),
    apiBaseUrl: API_BASE,
    envOverride: import.meta.env.VITE_API_BASE_URL || 'none'
  });
};
